import PartiesCard from './partiesCard';
import { useParams, useSearchParams } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import NoData from '../dashboard/noData';
import { ProfileUserEmpty } from '../icons';
import { Spin } from '../Ant';
import { useInView } from 'react-intersection-observer';
import { useTranslation } from 'react-i18next';
import SearchInputWithFilters from '../dashboard/SearchInputWithFilters';
import { useQueryClient } from '@tanstack/react-query';
import { useFetchPreliminaryInvoicePayers } from '../../hooks/useFetchPreliminaryInvoicePayers';
import { IPreliminaryInvoicePayer } from '../../interface/invoice';
import { ICustomerType } from '../../constants/customer';
import { useNavigationStack } from '../../context/NavigationStackContext';
import { ROUTES } from '../../constants/constants';
import SkeletonCardList from '../common/skeleton/skeletonCardList';
import CollapsibleListLayout from '../collapsibleListLayout/collapsibleListLayout';

const PreliminaryInvoicePayersTab = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { id } = useParams();
  const { push } = useNavigationStack();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialSearch = searchParams.get('payersSearch') || '';
  const [searchValue, setSearchValue] = useState<string>(initialSearch);
  const {
    refetch,
    isFetching,
    isLoading: isPayersLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    data: paginatedPayersData,
  } = useFetchPreliminaryInvoicePayers({
    id,
    sortBy: ['modifiedOn:DESC'],
    relations: [
      'privatePerson:fullName,privatePersonNumber',
      'company:name,companyNumber',
      'matterParty',
      'matterParty.roles',
      'matterParty.roles.matterPartyRole',
    ],
    search: searchValue,
    fields: ['sharePercentage', 'modifiedOn'],
  });

  const partiesSortedByPayerPercentage = useMemo(() => {
    return [...paginatedPayersData].sort((a, b) => b.sharePercentage - a.sharePercentage);
  }, [paginatedPayersData]);

  const handleGoToCustomer = (parties: IPreliminaryInvoicePayer) => {
    if (!parties?.company && !parties?.privatePerson) return;
    push(ROUTES.CUSTOMER_LISTING + `/${parties?.company?.id ?? parties?.privatePerson?.id}`);
  };
  useEffect(() => {
    setSearchParams({
      payersSearch: searchValue,
    });
  }, [searchValue]);
  const { ref, inView } = useInView({
    threshold: 0.5,
  });
  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage && paginatedPayersData?.length > 0) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, isFetchingNextPage]);
  const renderLoader = () => {
    if (isFetching) {
      return <Spin size="small" />;
    }
    return null;
  };
  return (
    <CollapsibleListLayout
      testId="preliminary-invoice-payers-listing"
      header={
        <SearchInputWithFilters
          searchTitle={t('matter:searchPayers')}
          handleSearch={(val) => {
            setSearchValue(val);
          }}
          searchValue={searchValue}
          handleRefresh={() => {
            queryClient.removeQueries({
              queryKey: ['preliminary-invoice-payers-list'],
            });
            return refetch();
          }}
        />
      }
    >
      {Object.entries(partiesSortedByPayerPercentage).length > 0 &&
        partiesSortedByPayerPercentage?.map((item: IPreliminaryInvoicePayer) => {
          return (
            <PartiesCard
              data-testid={`parties-card`}
              key={item.id}
              id={item?.id}
              customerName={item?.company?.name ?? item.privatePerson?.fullName ?? '-'}
              roles={item.matterParty?.roles ?? []}
              customerType={item.company ? ICustomerType.COMPANY : ICustomerType.PRIVATE_PERSON}
              sharePercentage={item?.sharePercentage?.toString() ?? ''}
              onPress={() => handleGoToCustomer(item)}
            />
          );
        })}
      {hasNextPage && <div ref={ref} className="loading-indicator" />}
      {(!paginatedPayersData ||
        (Object.entries(paginatedPayersData).length === 0 && !isFetching)) && (
        <div className="flex-grow d-flex justify-center items-center">
          <NoData
            header={t('preliminaryInvoice:noPayers')}
            subHeader={t('common:tryChangingSearchCriteria')}
            icon={<ProfileUserEmpty />}
          />
        </div>
      )}
      {isPayersLoading ? <SkeletonCardList rows={[1, 1]} /> : renderLoader()}
    </CollapsibleListLayout>
  );
};

export default PreliminaryInvoicePayersTab;
